import { useCallback } from 'react';

/**
 * Dar tabloda önemsiz sütunları sırayla gizler (ekran cilası, 2026-10-10): sütun hücreleri
 * `data-drop="1"`, `"2"` … ile işaretlenir; 1 önce gizlenir. Kutu tablo sığana kadar
 * `data-drop-level` düzeyini artırır, genişleyince geri alır. Sütun genişliği dile ve veriye göre
 * değiştiği için eşik sabit yazılmaz, ölçülür. Hiç sığmazsa son düzeyde kalır ve kutu kayar;
 * içerik kesilmez. Gizleme CSS'tedir (`styles.css` "Sütun gizleme"). 1 px yuvarlama sayılmaz.
 */
export function fitColumns(box: HTMLElement): number {
  let max = 0;
  for (const cell of box.querySelectorAll<HTMLElement>('[data-drop]')) {
    max = Math.max(max, Number(cell.dataset.drop) || 0);
  }
  let level = 0;
  delete box.dataset.dropLevel;
  while (box.scrollWidth > box.clientWidth + 1 && level < max) {
    level += 1;
    box.dataset.dropLevel = String(level);
  }
  return level;
}

/**
 * Tablo kutusunun ref'i (geri çağırmalı; kutu sonradan da açılabilir): kutunun genişliği (pencere)
 * ya da tablonun içeriği (sayfa, veri, dil) değiştikçe `fitColumns` bir sonraki karede yeniden
 * çalışır. Kareye ertelemek ResizeObserver döngü uyarısını önler (gizleme satır yüksekliğini
 * değiştirebilir).
 */
export function useFitColumns<T extends HTMLElement>(): (
  box: T | null,
) => (() => void) | undefined {
  return useCallback((box: T | null) => {
    if (!box) return undefined;
    fitColumns(box);
    if (typeof ResizeObserver === 'undefined' || typeof MutationObserver === 'undefined') {
      return undefined;
    }
    let frame = 0;
    let width = box.clientWidth;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        fitColumns(box);
      });
    };
    const resize = new ResizeObserver(() => {
      if (box.clientWidth === width) return;
      width = box.clientWidth;
      schedule();
    });
    resize.observe(box);
    const content = new MutationObserver(schedule);
    content.observe(box, { childList: true, subtree: true, characterData: true });
    return () => {
      resize.disconnect();
      content.disconnect();
      cancelAnimationFrame(frame);
    };
  }, []);
}
